import { AlpineData, LivewireData, LivewireMethods, NotificationDto, BaseNotification } from "@/js/interfaces/Components/alpinejs/NotificationMenu";
import { listen, sanitizeHtmlTag } from '@/js/app/support/utils';
import is from "../../support/is";
import { getBaseNotificationFromDto } from "../../services/NotificationAdapter";
import { createAlpineLivewireComponent } from "@nolikein/typed-alpinejs";
import { NotificationAlert } from "@/js/interfaces/Components/alpinejs/NotificationAlert";

/* Generated from the "artisan make:alpine" command */
export default {
    name: 'NotificationMenu',
    component: (
        // Component parameters...
        maxNotificationSize: number,
    ) => createAlpineLivewireComponent<AlpineData, LivewireData, LivewireMethods>({
        // Component properties ...
        notifications: [],
        loading: true,
        intervalId: null,

        /**
         * Initialize the component
         */
        init() {
            // Retrieve the notifications from the backend
            this.setInitialData();

            // Every minute, update the created_at_for_humans silently
            this.intervalId = (setInterval(() => {
                if (this.notifications.length > 0) {
                    this.updateTimestampSilently();
                }
            }, 60 * 1000)) as unknown as number;

            listen('add-user-notification', (e) => {
                const data = <object>e.detail;

                // Check the id
                if (!('id' in data)) {
                    throw Error('The notification menu received an item without id');
                }
                if (!is.string(data.id)) {
                    throw Error('The notification menu received an item id that is not a string');
                }

                // Check the message
                if (!('message' in data)) {
                    throw Error('The notification menu received an item without message');
                }
                if (!is.string(data.message)) {
                    throw Error('The notification menu received an item message that is not a string');
                }

                // Check the created_at dispplayed "for humans"
                if (!('created_at_for_humans' in data)) {
                    throw Error('The notification menu received an item without created_at_for_humans');
                }
                if (!is.string(data.created_at_for_humans)) {
                    throw Error('The notification menu received an item created_at_for_humans that is not a string');
                }

                // Check the read_at
                if (!('read_at' in data)) {
                    throw Error('The notification menu received an item without read_at');
                }
                if (!is.string(data.read_at) && null !== data.read_at) {
                    throw Error('The notification menu received an item read_at that is not a string or null');
                }

                // Avoid having more than defined notification quantity in the same page
                if (this.notifications.length > maxNotificationSize) {
                    this.notifications.pop();
                }
                this.notifications.unshift(data as BaseNotification);
            });

            listen('mark-notifications-as-read-frontside', () => {
                // Retrieve the list of unread notification then set them as read
                this.notifications
                    .filter(n => null === n.read_at)
                    .map(n => {
                        if (null === n.read_at) {
                            n.read_at = new Date().toISOString();
                        }
                    });
            });

            listen('notifications-flush', () => {
                this.notifications = [];
            });
        },

        destroy() {
            if (null !== this.intervalId) {
                clearInterval(this.intervalId);
            }
        },

        setInitialData(): void {
            this.$wire.$call('getNotifications')
                .then((data) => {
                    if (!is.array(data)) {
                        throw new Error('The notification data should be an array of notifications');
                    }

                    data.forEach((notificationDto: NotificationDto) => {
                        this.notifications.push(
                            getBaseNotificationFromDto(notificationDto),
                        );
                    });

                    this.loading = false;
                });
        },

        updateTimestampSilently(): void {
            this.$wire.$call('getNotifications')
                .then((data) => {
                    if (!is.array(data)) {
                        throw new Error('The notification data should be an array of notifications');
                    }

                    data.forEach((notificationDto: NotificationDto) => {
                        this.notifications.filter(n => notificationDto.id === n.id)[0].created_at_for_humans = notificationDto.created_at_for_humans;
                    });
                });
        },

        shouldDisplayMore(): boolean {
            return this.notifications.length > 0;
        },

        noOneNotification(): boolean {
            return 0 === this.notifications.length;
        },

        displayNewContentPing(): boolean {
            return false === this.loading && this.notifications.filter(n => null === n.read_at).length > 0;
        },

        getAlertMessage(alert: NotificationAlert): string {
            // Clean message to prevent XSS. Use the sanitizeHtmlTag function in utils
            return sanitizeHtmlTag(alert.message, { allow: ['a'] });
        },
    }),
};
